import React, { memo, useEffect, useState, useMemo, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import { validators, strings, objects } from "investira.sdk";
import {
  Stack,
  TextField,
  InputAdornment,
  Icon,
  Dialog,
  Divider,
  IconButton,
  Skeleton,
  DialogContent,
  useMediaQuery,
  Typography,
} from "investira.react.components";
import services from "../../../services";
import utils from "../../../utils";
import withResponseHandling from "../../../hoc/withResponseHandling";
import { acPesquisasGlobalChanged } from "../../../store/actions";
import SearchGlobalResultado from "./SearchGlobalResultado";
import { ATIVO_MERCADO, ATIVO_TIPO } from "@investirapri/financa.js/lib/enums";
import { PATHS, SETORES } from "../../../enums";

const SearchGlobal = memo((props) => {
  const RESULT_SIZE = 25;

  const storedGlobal = useSelector((store) => store.pesquisas.global);

  // const location = useLocation();
  const navigate = useNavigate();

  const storeDispatch = useDispatch();

  const [filters, setFilters] = useState({});

  const [isLoading, setLoading] = useState(false);
  const [pesquisa, setPesquisa] = useState("");
  const [data, setData] = useState([]);
  const [message, setMessage] = useState(null);
  const [open, setOpen] = useState(null);

  const inputRef = useRef();
  const placeholderRef = useRef();

  const handleOpen = (pEvent) => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    placeholderRef.current.blur();
    inputRef.current.blur();
    handleClearPesquisa();
  };

  const handleChange = (pEvent) => {
    const xValue = pEvent.currentTarget.value;

    setPesquisa(pEvent.currentTarget.value);
    handleOpen(pEvent);

    if (validators.isEmpty(xValue)) {
      handleClose();
      return;
    }
  };

  const openDrawerOnFocus = (pEvent) => {
    placeholderRef.current.blur();
    handleOpen(pEvent);
  };

  const handleClearPesquisa = (pLocation = {}) => {
    setPesquisa("");
    setData({});

    if (!validators.isEmpty(pLocation?.search)) {
      const xSearch = strings.querystringToObject(pLocation.search);

      if (!validators.isEmpty(xSearch.pesquisa)) {
        delete xSearch.pesquisa;
        const xQuery = objects.objectToQueryString(xSearch);
        navigate(`/empresas${xQuery}`);
      }
    }
  };

  const handleGoTo = (pEvent, pItem, pClearOnClose) => {
    pEvent.preventDefault();
    pEvent.stopPropagation();

    const xOrigem = pItem.pesquisa_origem;

    const xRoot = {
      pessoa: "/empresas",
      ativo: "/ativos",
      indicador: "/indicadores",
    };

    const xId = pItem.ativo_id || pItem.pessoa_id || pItem.indicador_id;

    let xPath = "";

    if (pItem.mercado) {
      xPath = PATHS[pItem.mercado.toLowerCase()].path;
    }

    handleClose();
    pClearOnClose && handleClearPesquisa();

    switch (xOrigem) {
      case "ativo":
        navigate(`${xRoot[xOrigem]}${xPath}/${xId}`);
        break;

      default:
        navigate(`${xRoot[xOrigem]}/${xId}`);
        break;
    }
  };

  const handleSearch = (pPesquisa, pFilters = {}, pOrigem, pMercado) => {
    const xFilters = objects.deleteNull({ ...pFilters, mercado: pMercado });

    const xPesquisa = validators.isEmpty(pPesquisa)
      ? storedGlobal.pesquisa.trim()
      : pPesquisa.trim();

    const xSearch = { pesquisa: xPesquisa, ...xFilters };
    const xSearchQueryString = objects.objectToQueryString(xSearch);

    const xRoot = {
      pessoa: "/empresas",
      ativo: "/ativos/busca",
    };

    const xUrl = `${xRoot[pOrigem]}${xSearchQueryString}`;

    handleClose();
    navigate(xUrl);
  };

  function verifySpecialChar(pStr) {
    const regex = /[./-]/;
    return regex.test(pStr);
  }

  function cleanSpecialChar(pStr) {
    const regex = /[./-]/g;
    if (verifySpecialChar(pStr)) {
      return pStr.replace(regex, "");
    }

    return pStr;
  }

  function isNumeric(pStr) {
    if (typeof pStr !== "string") {
      return false;
    }

    return /^[0-9]+$/.test(pStr);
  }

  const request = useMemo(() => {
    return utils.helpers.debounce((pParams, pRes, pErr, pFin) => {
      setLoading(true);
      services.pesquisas.principal(pParams, pRes, pErr, pFin);
    }, 1000);
  }, []);

  function removeDoubleSpaces(pStr) {
    return pStr.replace(/\s{2,}/g, " ").trim();
  }

  function removeEmptyItems(pArr) {
    return pArr.filter((item) => item !== "");
  }

  function formatSearch(pValue) {
    let xValue = removeDoubleSpaces(pValue);
    xValue = cleanSpecialChar(pValue);

    let xStrings = xValue.split(" ");
    xStrings = removeEmptyItems(xStrings);
    xStrings = xStrings.map((xString) => {
      return `+${xString}*`.trim();
    });

    const xSearchFormated = xStrings.join("");

    return xSearchFormated;
  }

  function processData(pData) {
    let xResult = [];

    if (validators.isEmpty(pData)) {
      return xResult;
    }

    xResult = Object.values(pData).map((xItem) => {
      let xNewItem = { ...xItem };

      if (!validators.isNull(xItem.pessoa_tipo_id)) {
        xNewItem.pessoa_tipo = utils.helpers
          .getPessoaTipoById(xItem.pessoa_tipo_id)
          .map((x) => x.descricao);
      }

      if (!validators.isNull(xItem.top_setor_id)) {
        xNewItem.top_setor = SETORES[xItem.top_setor_id].setor;
      }

      if (!validators.isNull(xItem.mercado)) {
        xNewItem.mercado_descricao = ATIVO_MERCADO[xItem.mercado].descricao;
      }

      if (!validators.isNull(xItem.ativo_tipo_id)) {
        xNewItem.ativo_tipo = ATIVO_TIPO[xItem.ativo_tipo_id].sigla;
      }

      return xNewItem;
    }, []);

    return xResult;
  }

  useEffect(() => {
    if (open) {
      placeholderRef.current.blur();
      inputRef.current.focus();

      const xData = processData(storedGlobal.resultado);
      setData(xData);

      // setData(storedGlobal.resultado);
    }
  }, [open, inputRef.current, placeholderRef.current]);

  useEffect(() => {
    if (pesquisa && pesquisa.length > 1 && !props.pageIsLoading) {
      setLoading(true);
      setData({});
      setMessage(null);

      const xPesquisa = formatSearch(pesquisa);

      request(
        {
          pesquisa: xPesquisa,
          cancelado: "null",
          ...(!isNumeric(xPesquisa) && { ...filters }),
          sort: "relevancia DESC, __mr__ DESC",
        },
        (rRes) => {
          const xData = processData(rRes.data);
          setData(xData);

          storeDispatch(
            acPesquisasGlobalChanged({
              pesquisa: pesquisa,
              resultado: rRes.data,
            })
          );

          if (validators.isEmpty(rRes.data)) {
            setMessage("Nenhum dado encontrado");
          }
        },
        (rErr) => {
          setMessage("Ocorreu um erro durante a busca");
        },
        () => {
          setLoading(false);
        }
      );
    }
  }, [pesquisa, request, filters]);

  const isUpMd = useMediaQuery((theme) => theme.breakpoints.up("md"));

  const xDialogProps = isUpMd
    ? {
        fullScreen: false,
        PaperProps: {
          sx: {
            pt: 3,
            pb: 2,
            px: 2,
          },
        },
        maxWidth: "lg",
      }
    : {
        fullScreen: true,
        PaperProps: {
          sx: {
            margin: 0,
            backgroundColor: "background.dark",
          },
        },
      };

  return (
    <>
      <Stack
        id="search-area"
        sx={{
          position: "relative",
          width: "100%",
          zIndex: "999",
        }}
      >
        {props.pageIsLoading ? (
          <Skeleton variant="rounded" width="100%" height={56} />
        ) : (
          <TextField
            id="placeholder-busca-empresa"
            inputRef={placeholderRef}
            value={pesquisa}
            onFocus={openDrawerOnFocus}
            //onInput={openDrawerOnFocus}
            variant="outlined"
            placeholder={"Pesquise por ativos, indicadores, empresas..."}
            fullWidth
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Icon iconName="find" size={21} color={"primary"} />
                </InputAdornment>
              ),
            }}
            disabled={props.pageIsLoading}
          />
        )}

        <Dialog
          {...xDialogProps}
          open={open}
          onClose={handleClose}
          keepMounted
          fullWidth
        >
          <DialogContent>
            <Stack direction="row" alignItems="center" spacing={2} mb={1}>
              <Stack>
                <IconButton onClick={handleClose} color={"primary"}>
                  <Icon iconName="arrow_left" size={21} />
                </IconButton>
              </Stack>
              <Stack flexGrow={1}>
                <TextField
                  id="busca-empresa"
                  inputRef={inputRef}
                  value={pesquisa}
                  onChange={handleChange}
                  sx={{ border: "none" }}
                  variant="outlined"
                  size={isUpMd ? "medium" : "small"}
                  placeholder={"Pesquise por ativos, indicadores, empresas..."}
                  fullWidth
                  disabled={props.pageIsLoading}
                />
              </Stack>
            </Stack>
            {storedGlobal.pesquisa && (
              <>
                <Divider />
                <Stack direction="row" alignItems="center" mx={2} my={1}>
                  <Typography variant="caption">
                    Resultado da busca por: <b>{storedGlobal.pesquisa}</b>
                  </Typography>
                </Stack>
                <Divider />
              </>
            )}

            <SearchGlobalResultado
              isLoading={isLoading}
              data={data}
              message={message}
              size={RESULT_SIZE}
              clearOnClose={props.clearOnClose}
              onClick={handleGoTo}
              onSearch={(pOrigem, pMercado) =>
                handleSearch(pesquisa, filters, pOrigem, pMercado)
              }
              // onSetFilters={setFilters}
              onClose={handleClose}
              onClearPesquisa={handleClearPesquisa}
            />
          </DialogContent>
        </Dialog>
      </Stack>
    </>
  );
});

export default withResponseHandling(SearchGlobal);
